import { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Icon from '../components/Icon';
import { useProfileActions } from '../components/ProfileCard';
import { EmptyState, Field, Loader, MatchRing, Modal, Page, Photo, Reveal, Select } from '../components/UI';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';
import { cmToFeet } from '../utils/format';

function Gallery({ photos = [], name, locked }) {
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const go = (d) => {
    setDir(d);
    setI((x) => (x + d + photos.length) % photos.length);
  };
  return (
    <div className="gallery">
      <div className="gallery__main">
        <AnimatePresence initial={false} custom={dir}>
          <motion.div
            key={i}
            className="gallery__slide"
            custom={dir}
            initial={{ x: `${dir * 100}%`, opacity: 0.4 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: `${-dir * 100}%`, opacity: 0.4 }}
            transition={{ type: 'spring', stiffness: 260, damping: 30 }}
            drag={photos.length > 1 ? 'x' : false}
            dragConstraints={{ left: 0, right: 0 }}
            onDragEnd={(e, info) => Math.abs(info.offset.x) > 60 && go(info.offset.x < 0 ? 1 : -1)}
          >
            <Photo src={photos[i]} alt={name} locked={locked} />
          </motion.div>
        </AnimatePresence>
        {photos.length > 1 && (
          <>
            <button className="gallery__nav gallery__nav--l" onClick={() => go(-1)} aria-label="Previous photo"><Icon name="chevronLeft" /></button>
            <button className="gallery__nav gallery__nav--r" onClick={() => go(1)} aria-label="Next photo"><Icon name="chevronRight" /></button>
            <div className="gallery__dots">
              {photos.map((_, k) => <span key={k} className={k === i ? 'is-on' : ''} />)}
            </div>
          </>
        )}
      </div>
      {photos.length > 1 && (
        <div className="gallery__thumbs">
          {photos.map((p, k) => (
            <button key={p} className={k === i ? 'is-on' : ''} onClick={() => { setDir(k > i ? 1 : -1); setI(k); }}>
              <img src={p} alt="" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Facts({ rows }) {
  const shown = rows.filter(([, v]) => v || v === 0);
  return (
    <dl className="facts">
      {shown.map(([k, v, icon]) => (
        <div key={k} className="facts__row">
          <dt>{icon && <Icon name={icon} size={15} />} {k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function Section({ title, icon, children, delay = 0 }) {
  return (
    <Reveal className="psection" delay={delay}>
      <h2><span className="psection__icon"><Icon name={icon} size={18} /></span>{title}</h2>
      {children}
    </Reveal>
  );
}

const REASONS = ['Fake profile', 'Inappropriate photos', 'Asking for money', 'Abusive behaviour', 'Already married', 'Other'];

export default function Profile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, toast, setUser, refreshCounts } = useApp();
  const { sendInterest, toggleShortlist } = useProfileActions();
  const [p, setP] = useState(null);
  const [error, setError] = useState(null);
  const [contact, setContact] = useState(null);
  const [gate, setGate] = useState(null);
  const [report, setReport] = useState(false);
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [menu, setMenu] = useState(false);

  const load = useCallback(() => api(`/profiles/${id}`).then((d) => setP(d.profile)).catch((e) => setError(e.message)), [id]);
  useEffect(() => {
    load();
  }, [load]);

  if (error) return <Page><div className="container"><EmptyState icon="user" title="Profile unavailable" text={error} action={<Link to="/matches" className="btn btn--primary">Back to matches</Link>} /></div></Page>;
  if (!p) return <Page><Loader label="Opening profile…" /></Page>;

  const respond = async (status) => {
    try {
      const r = await api(`/interests/${p.interest.id}`, { method: 'PUT', body: { status } });
      toast(r.message, status === 'accepted' ? 'success' : 'default');
      load();
      refreshCounts();
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const viewContact = async () => {
    try {
      const r = await api(`/profiles/${p.profileId}/contact`);
      setContact(r.contact);
    } catch (e) {
      setGate({ reason: e.data?.reason, message: e.message });
    }
  };

  const block = async () => {
    try {
      const r = await api(`/profiles/${p.profileId}/block`, { method: 'POST' });
      toast(r.message);
      setUser({ ...user, blocked: r.blocked ? [...user.blocked, p.profileId] : user.blocked.filter((x) => x !== p.profileId) });
      if (r.blocked) navigate('/matches');
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const sendReport = async () => {
    try {
      const r = await api(`/profiles/${p.profileId}/report`, { method: 'POST', body: { reason, details } });
      toast(r.message, 'success');
      setReport(false);
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const it = p.interest;
  const pp = p.partnerPrefs || {};
  const fits = (ok) => (ok ? <Icon name="check" size={15} stroke={2.6} className="fit fit--yes" /> : <Icon name="x" size={14} stroke={2.4} className="fit fit--no" />);
  const listFit = (list, v) => !list?.length || list.includes(v);
  const prefRows = [
    ['Age', pp.minAge ? `${pp.minAge} – ${pp.maxAge} yrs` : 'Open', user.age >= (pp.minAge || 0) && user.age <= (pp.maxAge || 99)],
    ['Religion', pp.religions?.length ? pp.religions.join(', ') : 'Open to all', listFit(pp.religions, user.religion)],
    ['Marital status', pp.maritalStatus?.length ? pp.maritalStatus.join(', ') : 'Any', listFit(pp.maritalStatus, user.maritalStatus)],
    ['Location', pp.cities?.length ? pp.cities.join(', ') : 'Anywhere in India', listFit(pp.cities, user.city)],
    ['Profession', pp.professions?.length ? pp.professions.join(', ') : 'Any', listFit(pp.professions, user.profession)],
    ['Diet', pp.diets?.length ? pp.diets.join(', ') : 'No preference', listFit(pp.diets, user.diet)],
  ];
  const fitCount = prefRows.filter((r) => r[2]).length;

  let primary;
  if (p.isSelf) primary = <Link to="/me/edit" className="btn btn--primary"><Icon name="edit" size={17} /> Edit my profile</Link>;
  else if (!it) primary = <motion.button whileTap={{ scale: 0.95 }} className="btn btn--primary" onClick={() => sendInterest(p, load)}><Icon name="heart" size={17} fill="currentColor" stroke={0} /> Send interest</motion.button>;
  else if (it.status === 'pending' && it.direction === 'received')
    primary = (
      <>
        <motion.button whileTap={{ scale: 0.95 }} className="btn btn--primary" onClick={() => respond('accepted')}><Icon name="check" size={17} stroke={2.4} /> Accept</motion.button>
        <button className="btn btn--ghost" onClick={() => respond('declined')}>Decline</button>
      </>
    );
  else if (it.status === 'pending') primary = <span className="btn btn--soft is-static"><Icon name="check" size={17} /> Interest sent · awaiting reply</span>;
  else if (it.status === 'accepted') primary = <Link to={`/messages/${p.profileId}`} className="btn btn--teal"><Icon name="chat" size={17} /> Chat now</Link>;
  else primary = <span className="btn btn--soft is-static">Interest declined</span>;

  return (
    <Page className="profile">
      <div className="container profile__grid">
        <div className="profile__side">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
            <Gallery photos={p.photos?.length ? p.photos : [p.photo]} name={p.name} locked={p.photoLocked} />
          </motion.div>
          {!p.isSelf && (
            <Reveal className="contact-card">
              <h3><Icon name="phone" size={17} /> Contact details</h3>
              <AnimatePresence mode="wait">
                {contact ? (
                  <motion.div key="c" initial={{ opacity: 0, rotateX: 90 }} animate={{ opacity: 1, rotateX: 0 }}>
                    <p><Icon name="phone" size={15} /> {contact.phone}</p>
                    <p><Icon name="mail" size={15} /> {contact.email}</p>
                  </motion.div>
                ) : (
                  <motion.div key="h" exit={{ opacity: 0, rotateX: -90 }}>
                    <p className="blurred">+91 98••• •••••</p>
                    <p className="blurred">{(p.firstName || '').replace('Dr. ', '').toLowerCase()}@••••.com</p>
                    <button className="btn btn--ghost btn--block btn--sm" onClick={viewContact}><Icon name="eye" size={15} /> View contact</button>
                  </motion.div>
                )}
              </AnimatePresence>
            </Reveal>
          )}
        </div>

        <div className="profile__main">
          <motion.header className="phead" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <div className="phead__top">
              <div>
                <div className="phead__badges">
                  {p.verified && <span className="pill pill--teal"><Icon name="shield" size={13} /> Verified</span>}
                  {p.fromMedhire && <span className="pill pill--teal"><Icon name="steth" size={13} /> MedHire</span>}
                  {p.premium && <span className="pill pill--gold"><Icon name="crown" size={13} /> Premium</span>}
                  <span className={`pill ${p.online ? 'pill--green' : 'pill--soft'}`}>{p.online ? '● Online now' : p.lastSeen || 'Offline'}</span>
                </div>
                <h1>{p.name}</h1>
                <p className="phead__meta">
                  {[p.age && `${p.age} yrs`, cmToFeet(p.heightCm), p.religion, p.motherTongue, p.maritalStatus].filter(Boolean).join(' · ')}
                </p>
                <p className="phead__job"><Icon name="briefcase" size={16} /> {[p.designation || p.specialty, p.employer].filter(Boolean).join(' at ') || p.profession}</p>
                <p className="phead__job"><Icon name="pin" size={16} /> {[p.city, p.state].filter(Boolean).join(', ')}</p>
                <small className="muted">Profile ID {p.profileId} · Created by {p.createdBy || 'Self'}</small>
              </div>
              {!p.isSelf && (
                <div className="phead__scores">
                  <MatchRing value={p.matchScore} size={78} />
                  <Link to={`/kundli/${p.profileId}`} className="kundli-chip">
                    <Icon name="moon" size={16} />
                    <span><strong>{p.kundliTotal}/36</strong><small>Gunas</small></span>
                  </Link>
                </div>
              )}
            </div>
            {p.matchReasons?.length > 0 && !p.isSelf && (
              <div className="phead__reasons">
                <small className="muted">You match on</small>
                {p.matchReasons.map((r, i) => (
                  <motion.span key={r} className="tag tag--rose" initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 + i * 0.08 }}>
                    <Icon name="check" size={12} stroke={2.6} /> {r}
                  </motion.span>
                ))}
              </div>
            )}
          </motion.header>

          <div className="actionbar">
            {primary}
            {!p.isSelf && (
              <>
                {it?.status !== 'accepted' && <Link to={`/messages/${p.profileId}`} className="btn btn--ghost"><Icon name="chat" size={17} /> Message</Link>}
                <motion.button whileTap={{ scale: 0.85 }} className={`icon-btn icon-btn--lg ${p.shortlisted ? 'is-gold' : ''}`} onClick={() => toggleShortlist(p, (s) => setP({ ...p, shortlisted: s }))} aria-label="Shortlist">
                  <Icon name="star" fill={p.shortlisted ? 'currentColor' : 'none'} />
                </motion.button>
                <div className="more">
                  <button className="icon-btn icon-btn--lg" onClick={() => setMenu(!menu)} aria-label="More"><Icon name="menu" /></button>
                  <AnimatePresence>
                    {menu && (
                      <motion.div className="more__menu" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} onMouseLeave={() => setMenu(false)}>
                        <button onClick={() => { setMenu(false); block(); }}><Icon name="block" size={16} /> Block profile</button>
                        <button onClick={() => { setMenu(false); setReport(true); }}><Icon name="flag" size={16} /> Report profile</button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            )}
          </div>

          {p.about && (
            <Section title={`About ${p.firstName?.replace('Dr. ', '') || ''}`} icon="heart">
              <p className="about">{p.about}</p>
              {p.hobbies?.length > 0 && <div className="chips chips--static">{p.hobbies.map((h) => <span key={h} className="tag">{h}</span>)}</div>}
            </Section>
          )}

          <Section title="Basic & lifestyle" icon="user">
            <Facts
              rows={[
                ['Age', p.age && `${p.age} years`, 'calendar'],
                ['Height', cmToFeet(p.heightCm), 'ruler'],
                ['Marital status', p.maritalStatus],
                ['Religion / community', [p.religion, p.caste].filter(Boolean).join(' · ')],
                ['Mother tongue', p.motherTongue],
                ['Languages', p.languages?.join(', ')],
                ['Diet', p.diet],
                ['Smoke / drink', [p.smoke, p.drink].filter(Boolean).join(' / ')],
                ['Lives in', [p.city, p.state, p.country].filter(Boolean).join(', '), 'pin'],
                ['Hometown', p.homeTown],
              ]}
            />
          </Section>

          <Section title="Education & career" icon="cap">
            <Facts
              rows={[
                ['Highest qualification', p.highestQualification, 'cap'],
                ['Profession', p.profession, 'steth'],
                ['Specialty', p.specialty],
                ['Works at', [p.designation, p.employer].filter(Boolean).join(', '), 'briefcase'],
                ['Experience', p.experienceYears ? `${p.experienceYears} years` : null],
                ['Annual income', p.income || 'Visible to Premium members', 'rupee'],
              ]}
            />
            {(p.education?.length > 0 || p.workHistory?.length > 0) && (
              <div className="timeline">
                {p.education?.map((e, i) => (
                  <div key={`e${i}`} className="timeline__item">
                    <span className="timeline__dot tint-violet"><Icon name="cap" size={14} /></span>
                    <div><strong>{[e.degree, e.specialization].filter(Boolean).join(' · ')}</strong><small>{e.institution} {e.year && `· ${e.year}`}</small></div>
                  </div>
                ))}
                {p.workHistory?.map((w, i) => (
                  <div key={`w${i}`} className="timeline__item">
                    <span className="timeline__dot tint-teal"><Icon name="briefcase" size={14} /></span>
                    <div><strong>{w.title}</strong><small>{w.organization} · {w.from} – {w.to || 'Present'}</small></div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          <Section title="Family" icon="family">
            <Facts
              rows={[
                ["Father's occupation", p.family?.fatherOccupation],
                ["Mother's occupation", p.family?.motherOccupation],
                ['Siblings', p.family && `${p.family.brothers || 0} brother(s), ${p.family.sisters || 0} sister(s)`],
                ['Family type', p.family?.familyType],
                ['Family values', p.family?.familyValues],
                ['Native place', p.family?.nativePlace],
              ]}
            />
          </Section>

          <Section title="Horoscope" icon="moon">
            <Facts
              rows={[
                ['Rashi', p.horoscope?.rashi],
                ['Nakshatra', p.horoscope?.nakshatra],
                ['Manglik', p.horoscope?.manglik],
                ['Birth time', p.horoscope?.birthTime],
                ['Birth place', p.horoscope?.birthPlace],
              ]}
            />
            {!p.isSelf && (
              <Link to={`/kundli/${p.profileId}`} className="kundli-banner">
                <Icon name="moon" size={22} />
                <span><strong>{p.kundliTotal} of 36 gunas match</strong><small>See the full Ashtakoot report</small></span>
                <Icon name="arrowRight" size={18} />
              </Link>
            )}
          </Section>

          <Section title="What they're looking for" icon="sparkle">
            {pp.about && <p className="about">{pp.about}</p>}
            {!p.isSelf && <p className="fit-summary">You match <strong>{fitCount} of {prefRows.length}</strong> of {p.firstName?.replace('Dr. ', '')}'s preferences</p>}
            <dl className="facts">
              {prefRows.map(([k, v, ok]) => (
                <div key={k} className="facts__row">
                  <dt>{k}</dt>
                  <dd>{v} {!p.isSelf && fits(ok)}</dd>
                </div>
              ))}
            </dl>
          </Section>
        </div>
      </div>

      <Modal open={!!gate} onClose={() => setGate(null)} title="Contact details">
        <div className="gate">
          <div className="gate__icon"><Icon name={gate?.reason === 'interest' ? 'heart' : gate?.reason === 'hidden' ? 'lock' : 'crown'} size={30} /></div>
          <p>{gate?.message}</p>
          {(gate?.reason === 'premium' || gate?.reason === 'quota') && <Link to="/premium" className="btn btn--gold btn--block shine">See Premium plans</Link>}
          {gate?.reason === 'interest' && !it && <button className="btn btn--primary btn--block" onClick={() => { setGate(null); sendInterest(p, load); }}>Send interest</button>}
        </div>
      </Modal>

      <Modal open={report} onClose={() => setReport(false)} title="Report this profile">
        <div className="stack">
          <Field label="Reason"><Select value={reason} onChange={setReason} options={REASONS} /></Field>
          <Field label="Details (optional)"><textarea className="input" rows={3} value={details} onChange={(e) => setDetails(e.target.value)} /></Field>
          <button className="btn btn--primary btn--block" disabled={!reason} onClick={sendReport}>Submit report</button>
        </div>
      </Modal>
    </Page>
  );
}
